import { AxiosError } from 'axios';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { Trash, Upload, X } from 'react-feather';
import { useForm } from 'react-hook-form';
import Modal from 'react-modal';
import { HttpExceptionDto } from '../interfaces/http-exception.dto';
import { DetailIgnoredRowDto } from '../interfaces/import-csv-result.dto';
import { ImportPurchaseOrderCsvDto } from '../interfaces/import-purchase-order-csv.dto';
import { OrganizationClient } from '../interfaces/organization-client';
import { api } from '../services/api';
import myToastr from '../services/toastr';
import { useAppSelector } from '../store/hooks';

interface Props {
  closeModal: (purchaseOrderId?: number) => void;
  show: boolean;
}

interface FormData {
  organizationClientId: number;
}

const ImportPurchaseOrderCsvModal = ({ show, closeModal }: Props) => {
  const organization = useAppSelector((state) => state.auth.organization)!;
  const [organizationClients, setOrganizationClients] = useState<OrganizationClient[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const csvInputFileRef = useRef<any>(null);
  const [requesting, setRequesting] = useState<boolean>(false);
  const [importingErrors, setImportingErrors] = useState<DetailIgnoredRowDto[]>([]);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<FormData>({
    mode: 'onSubmit',
    defaultValues: {
      organizationClientId: -1,
    },
  });

  const getOrganizationClients = async () => {
    try {
      const ocs = await api.getOrganizationClients(organization.id);
      setOrganizationClients(ocs.filter((oc) => oc.organizationId === organization.id));
    } catch (e: any) {
      const axiosError: AxiosError<HttpExceptionDto> = e;
      if (axiosError.response?.data) {
        const httpExceptionDto: HttpExceptionDto = axiosError.response.data;
        myToastr.error(httpExceptionDto.message);
      }
    }
  };

  const removeFile = () => {
    setFile(null);
    setImportingErrors([]);
  };

  const onSelectedFile = (event: any) => {
    if (event.target.files.length === 0) {
      return;
    }
    const file: File = event.target.files[0];
    if (file.type !== 'text/csv') {
      myToastr.error('El fichero seleccionado no es un CSV');
      return;
    }
    setFile(file);
    setImportingErrors([]);
  };

  const close = () => {
    closeModal();
  };

  const downloadCsvExample = () => {
    const filePath = `${process.env.PUBLIC_URL}/files/sample/purchase_order.csv`;
    const link = document.createElement('a');
    link.href = filePath;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onSubmit = async (formData: FormData) => {
    if (!file) {
      myToastr.error('Debes seleccionar un fichero CSV');
      return;
    }
    setRequesting(true);
    try {
      const payload: ImportPurchaseOrderCsvDto = {
        file: file!,
        organizationClientId: formData.organizationClientId,
      };
      const purchaseOrderId = await api.importPurchaseOrder(payload);
      closeModal(purchaseOrderId);
      myToastr.success('Pedido importado correctamente');
    } catch (e: any) {
      const { errors } = e.response.data as { errors: DetailIgnoredRowDto[] };
      setImportingErrors(errors);
      myToastr.error('Se han encontrado errores en el fichero CSV. Revisa los detalles.');
    }
    setRequesting(false);
  };

  useEffect(() => {
    if (show) {
      getOrganizationClients();
    } else {
      reset();
      setFile(null);
    }
  }, [show]);

  return (
    <Modal className="vercomi-modal my-form import-csv-modal" isOpen={show} onRequestClose={close} shouldCloseOnOverlayClick={false}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="content">
          <div className="d-flex mb-3">
            <div className="title text-center flex-grow-1">Importar pedido</div>
            <button type="button" className="close-button-modal" onClick={close} title="Cerrar">
              <X size={16} />
            </button>
          </div>
          <div className="noselect">
            <p>
              Descarga nuestro CSV de ejemplo{' '}
              <span className="download-example-csv" onClick={downloadCsvExample}>
                aquí
              </span>
              .<span></span>
            </p>
            <p>Importante: se realizará la importación siempre y cuando no se encuentren errores en ninguna de las filas del CSV.</p>
            <div className="container-import-csv-file">
              {file === null ? (
                <div title="Seleccionar fichero" className="d-flex align-items-center" onClick={() => csvInputFileRef.current.click()}>
                  <Upload className="me-2" color="#A0AEC1" size={18} />
                  <span>Importa tu CSV</span>
                </div>
              ) : (
                <div title="Elimina fichero" className="d-flex align-items-center" onClick={removeFile}>
                  <Trash className="me-2" color="#A0AEC1" size={18} />
                  <span>{file.name}</span>
                </div>
              )}
              <input
                ref={csvInputFileRef}
                type="file"
                accept=".csv"
                onClick={(event: any) => {
                  event.target.value = null;
                }}
                onChange={onSelectedFile}
              />
            </div>
            <div className="row">
              <div className="col-6">
                <div className={clsx('input-name', { error: errors?.organizationClientId })}>Cliente *</div>
                <select
                  {...register('organizationClientId', {
                    required: true,
                    min: {
                      value: 1,
                      message: 'Debes seleccionar un cliente',
                    },
                    valueAsNumber: true,
                  })}
                  className={clsx({ error: errors?.organizationClientId })}
                >
                  <option value={-1}>Selecciona un cliente</option>
                  {organizationClients.map((organizationClient) => (
                    <option disabled={!organizationClient.relatedStoreId} key={organizationClient.id} value={organizationClient.id}>
                      {organizationClient.relatedOrganization.name}
                    </option>
                  ))}
                </select>
                {errors.organizationClientId && <div className="error-message">{errors.organizationClientId.message}</div>}
              </div>
            </div>
          </div>
          {importingErrors.length > 0 && (
            <div className="container-import-result mt-4">
              <div className="container-table">
                <table>
                  <thead>
                    <tr>
                      <th>Fila</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importingErrors.map((detailIgnoredRowDto, index) => {
                      return (
                        <tr key={index}>
                          <td>{detailIgnoredRowDto.row}</td>
                          <td>{detailIgnoredRowDto.reason}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-row">
          <button className="modal-button" disabled={requesting} type="submit">
            Finalizar
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ImportPurchaseOrderCsvModal;
